import React, { useMemo } from 'react'
import AppDropdown from '../../../../components/AppDropdown'
import {
  StepHeader,
  StepCard,
  DROPDOWN_TRIGGER_CLASS,
} from '../components/StepLayout'

const AGG_FUNCS = [
  { value: 'SUM', label: 'Sum (Total)' },
  { value: 'AVG', label: 'Average (Mean)' },
  { value: 'COUNT', label: 'Count (Rows)' },
  { value: 'DISTINCT_COUNT', label: 'Distinct Count' },
  { value: 'MIN', label: 'Minimum' },
  { value: 'MAX', label: 'Maximum' },
]

export default function Step7Aggregations({
  aggregations = [],
  setAggregations,
  selectedFields = [],
  allFields = [],
}) {
  const numericFields = (selectedFields.length > 0 ? selectedFields : allFields).filter(
    (f) => ['number', 'currency', 'percentage'].includes(f.dataType) || !f.dataType
  )

  const fieldOptions = useMemo(
    () => numericFields.map((f) => ({ value: f.key, label: f.label })),
    [numericFields]
  )

  function addAggregation() {
    const firstField = numericFields[0]?.key || ''
    setAggregations((prev) => [...prev, { field: firstField, func: 'SUM' }])
  }

  function removeAggregation(index) {
    setAggregations((prev) => prev.filter((_, i) => i !== index))
  }

  function updateAggregation(index, prop, value) {
    setAggregations((prev) =>
      prev.map((agg, i) => (i === index ? { ...agg, [prop]: value } : agg))
    )
  }

  return (
    <div className="space-y-3 max-w-4xl mx-auto py-1">
      <StepHeader
        title="Numeric Summarization & Aggregations"
        subtitle="Specify summary metrics (Sum, Average, Min, Max) calculated across groups and grand totals."
        actions={
          <button
            type="button"
            onClick={addAggregation}
            className="h-8 text-xs font-bold text-primary border border-primary/30 px-2.5 rounded-[8px] hover:bg-primary/5 flex items-center gap-1 transition-colors"
          >
            <span className="material-symbols-outlined text-sm">add</span>
            Add Aggregation
          </button>
        }
      />

      <StepCard>
        {aggregations.length === 0 ? (
          <div className="text-center py-6 border border-dashed border-surface-container-high rounded-[8px] text-xs text-on-surface-variant">
            <span className="material-symbols-outlined text-2xl text-slate-300 mb-1 block">functions</span>
            No explicit aggregations configured. Standard totals will be calculated automatically on numeric columns.
          </div>
        ) : (
          <div className="space-y-2">
            {aggregations.map((agg, index) => (
              <div
                key={index}
                className="flex flex-col sm:flex-row sm:items-center gap-2.5 p-2.5 bg-surface rounded-[8px] border border-surface-container-high text-xs"
              >
                <span className="font-bold text-primary shrink-0 text-xs w-20">Summary {index + 1}</span>

                <div className="flex-1 min-w-0">
                  <AppDropdown
                    value={agg.field}
                    onChange={(val) => updateAggregation(index, 'field', val)}
                    options={fieldOptions}
                    triggerClassName={DROPDOWN_TRIGGER_CLASS}
                  />
                </div>

                <div className="w-full sm:w-48 shrink-0">
                  <AppDropdown
                    value={agg.func}
                    onChange={(val) => updateAggregation(index, 'func', val)}
                    options={AGG_FUNCS}
                    triggerClassName={DROPDOWN_TRIGGER_CLASS}
                  />
                </div>

                <button
                  type="button"
                  onClick={() => removeAggregation(index)}
                  className="p-1.5 text-slate-400 hover:text-error hover:bg-surface-container rounded-[8px] self-end sm:self-center shrink-0 transition-colors"
                  title="Remove Summary Metric"
                >
                  <span className="material-symbols-outlined text-base">close</span>
                </button>
              </div>
            ))}
          </div>
        )}
      </StepCard>
    </div>
  )
}
